import {
  BOOKING_QUERY_KEYS,
  BookingQuery,
  isValidDate,
  isValidEventType,
  isValidGuestCount,
  isValidId,
  isValidName,
  isValidStep,
  isValidTime,
  timeIsAfter,
} from "./bookingQuerySchema";
import { categoryKeyOf } from "../../../home/homeTiles";

/**
 * URL ↔ booking state, shared by every room-shaped flow (ADR-0026, phase 1
 * slice B). Merged from the identical-but-for-names copies under
 * `roomRental/common/url/` and `venueReservation/common/url/`.
 *
 * **Invalid parameters are dropped, never fatal.** Partial hydration beats a
 * full reset: a customer who followed a link with one bad value should land in
 * the flow with everything else intact, not at the start with no explanation.
 *
 * ONE deliberate behaviour change, both copies considered: the room parser
 * accepted a `checkOutDate` EARLIER than its `checkInDate` and the venue parser
 * dropped it. The stricter rule wins — a stay that ends before it begins is not
 * a partial answer, it is a wrong one, and carrying it into Redux produced a
 * negative night count downstream. Pinned by a test.
 */

/**
 * Cross-field rule for `endTime`: on a same-day booking (no `checkOutDate`, or
 * equal to `checkInDate`) it must be later than `startTime`; across days any
 * time of day is valid — 1 Aug 17:00 → 2 Aug 13:00 is a legitimate booking.
 */
function isAcceptableEndTime(out: BookingQuery, endTime: string): boolean {
  const isMultiDay =
    !!out.checkInDate &&
    !!out.checkOutDate &&
    out.checkOutDate > out.checkInDate;
  return isMultiDay || !out.startTime || timeIsAfter(endTime, out.startTime);
}

/**
 * A validated string parameter, or `undefined`. Twelve fields read the same
 * way, so the shape is written once — the alternative is twelve near-identical
 * guards, which is what pushed the merged parser past the complexity budget and
 * is also where a typo hides best.
 */
function readString(
  params: URLSearchParams,
  key: string,
  isValid: (value: unknown) => boolean
): string | undefined {
  const raw = params.get(key);
  return raw && isValid(raw) ? raw : undefined;
}

/** A validated numeric parameter, or `undefined`. An absent key and an
 *  unparseable one are the same answer: the field was not supplied. */
function readNumber(
  params: URLSearchParams,
  key: string,
  isValid: (value: unknown) => boolean
): number | undefined {
  const raw = params.get(key);
  if (raw === null) return undefined;
  const parsed = Number(raw);
  return isValid(parsed) ? parsed : undefined;
}

/** Assign only when the value survived validation, so an absent field stays
 *  absent rather than becoming an explicit `undefined` in the object. */
function assign<K extends keyof BookingQuery>(
  out: BookingQuery,
  key: K,
  value: BookingQuery[K] | undefined
): void {
  if (value !== undefined) out[key] = value;
}

/** Parse a URL search string into a validated `BookingQuery`. */
export function parseBookingQuery(search: string): BookingQuery {
  const normalized = search.startsWith("?") ? search.substring(1) : search;
  const params = new URLSearchParams(normalized);
  const out: BookingQuery = {};

  // Read through the SAME normalise-then-validate reader the entry URL uses,
  // so a key cannot be accepted on the home screen and rejected at step 3 (or
  // the reverse) — and so both sides carry the same string, not two spellings
  // of it.
  assign(out, "category", categoryKeyOf(params.get("category")));
  assign(out, "city", readString(params, "city", isValidName));
  assign(out, "district", readString(params, "district", isValidName));
  assign(out, "checkInDate", readString(params, "checkInDate", isValidDate));

  // Read after `checkInDate`, because the rule below compares against it: a
  // stay that ends before it begins is dropped rather than carried into Redux.
  const checkOutDate = readString(params, "checkOutDate", isValidDate);
  if (checkOutDate && (!out.checkInDate || checkOutDate >= out.checkInDate)) {
    out.checkOutDate = checkOutDate;
  }

  assign(out, "startTime", readString(params, "startTime", isValidTime));

  // Read after `startTime` and both dates — `isAcceptableEndTime` needs all
  // three to tell a same-day booking from one that runs overnight.
  const endTime = readString(params, "endTime", isValidTime);
  if (endTime && isAcceptableEndTime(out, endTime)) out.endTime = endTime;

  assign(out, "eventType", readString(params, "eventType", isValidEventType));
  assign(
    out,
    "numberOfGuests",
    readNumber(params, "numberOfGuests", isValidGuestCount)
  );
  assign(out, "objectId", readString(params, "objectId", isValidId));
  assign(out, "step", readNumber(params, "step", isValidStep));

  return out;
}

export function stringifyBookingQuery(query: BookingQuery): string {
  const params = new URLSearchParams();
  for (const key of BOOKING_QUERY_KEYS) {
    // `unknown`, deliberately: the declared type says the value cannot be null,
    // so a `=== null` check against it reads as dead code to a type-aware
    // linter. It is not dead at runtime — a caller reaching this through a
    // loosely typed patch can still hand us one, and `String(null)` would put
    // the literal "null" in the customer's URL.
    const value: unknown = query[key];
    if (value === null || value === undefined) continue;
    params.set(key, String(value));
  }
  return params.toString();
}

/**
 * `bookingSearch` (already stringified) plus every parameter of `currentSearch`
 * that is NOT a booking key, in its original order.
 *
 * The booking flow shares its search string with parameters it does not own —
 * the in-place terms and withdrawal sheets (`?terms=1`, `?withdrawal=1`, see
 * `useInPlaceSheet`) and anything a link brings along. A sync that rebuilds the
 * query from booking keys alone drops them, which closed the terms sheet the
 * moment it opened (regression of 2026-09-14). Booking keys are never copied
 * from `currentSearch`: they come from `bookingSearch` only, so a stale or
 * invalid booking value cannot survive through this path.
 */
export function withForeignParams(
  bookingSearch: string,
  currentSearch: string
): string {
  const normalized = currentSearch.startsWith("?")
    ? currentSearch.substring(1)
    : currentSearch;
  const own = new Set<string>(BOOKING_QUERY_KEYS);
  const out = new URLSearchParams(bookingSearch);
  new URLSearchParams(normalized).forEach((value, key) => {
    if (!own.has(key)) out.append(key, value);
  });
  return out.toString();
}

export function mergeBookingQuery(
  base: BookingQuery,
  patch: Partial<BookingQuery>
): BookingQuery {
  const filtered = Object.fromEntries(
    Object.entries(patch).filter(([, v]) => (v as unknown) !== undefined)
  ) as Partial<BookingQuery>;
  return { ...base, ...filtered };
}

export function updateBookingQuery(
  currentSearch: string,
  patch: Partial<BookingQuery>
): string {
  const current = parseBookingQuery(currentSearch);
  const merged = mergeBookingQuery(current, patch);
  return stringifyBookingQuery(merged);
}
